import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { MainContentBox, MainDataGridNoRowHover, DataGridListScrollBox, PageTitleToolbarGrid } from '../../util/SharedStyles';
import { GridRenderCellParams, GridValueGetterParams, GridColumns, GridRowModel, GridOverlay, GridSortModel, useGridApiContext, GridSortItem, GridSelectionModel, GridToolbarContainer, GridCsvExportOptions, GridApi } from '@mui/x-data-grid-pro';
import { Button, CircularProgress, Link, Tooltip, Typography, styled } from '@mui/material';
import { debounce } from 'lodash';

import { useTitle, capitalizeWithUnderscoreRemoval, PremadeEventLogFilterSet } from '../../util/Common';
import { Viewer, defaultGridListPageSize, eventLogErrorsFilterDate } from '../../util/Constants';
import { getShortDateString, getTimeString } from '../../util/DateTimeUtility';

import PageTitleBar from '../../components/PageTitleBar';
import ErrorMessage from '../../components/ErrorMessage';
import NoResultsMessage from '../../components/NoResultsMessage';
import { clearState, selectError, selectRequestsInFlight, selectEventLogList, selectEventLogPagingResult } from './EventLogsSlice';
import { fetchEventLogList } from './EventLogsActions';
import MainDataGridLoadingSkeleton from '../../components/MainDataGridLoadingSkeleton';
import EventLogsFilterBar from '../../components/filters/EventLogsFilterBar';
import FiltersButton from '../../components/buttons/FiltersButton';
import GridCellDualVert from '../../components/listItems/GridCellDualVert';
import { GetMessagingLogsInput, MessageLogOrderByInput, OrderDirection, DocumentStatus, ProcessName } from '../../gql-types.generated';
import { viewerCanEdit } from '../../util/ViewerUtility';
import GridListFooter from '../../components/paging/GridListFooter';

const DetailsLink = styled(Link)((props) => ({
    cursor: 'pointer',
}));

const ToolbarButton = styled(Button)`
     margin-right: 4px;
`;

interface EventLogsProps {
    viewer: Viewer | undefined;
}

const EventLogs: React.FC<EventLogsProps> = (props) => {
    const { premadeQuery } = useParams();
    const { viewer } = props;
    const dispatch = useAppDispatch();    
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [contentAreaHeight, setContentAreaHeight] = useState('200px'); //need an actual default height so grid doesn't error on load
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [filtersCleared, setFiltersCleared] = useState(false);
    const [filterClientName, setFilterClientName] = useState<string | undefined>(undefined);
    const [filterClientId, setFilterClientId] = useState<string | undefined>(undefined);
    const [filterPartnerName, setFilterPartnerName] = useState<string | undefined>(undefined);
    const [filterPartnerId, setFilterPartnerId] = useState<string | undefined>(undefined);
    const [filterPO, setFilterPO] = useState<string | undefined>(undefined);
    const [filterDocNumber, setFilterDocNumber] = useState<string | undefined>(undefined);
    const [filterProcessName, setFilterProcessName] = useState<ProcessName | undefined>(undefined);
    const [filterStatus, setFilterStatus] = useState<DocumentStatus | undefined>(undefined);
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(undefined);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(undefined);
    const [filterDirection, setFilterDirection] = useState<string | undefined>(undefined);
    const [filterDocumentType, setFilterDocumentType] = useState<string | undefined>(undefined);
    const [filterFileName, setFilterFileName] = useState<string | undefined>(undefined);
    const [filterCount, setFilterCount] = useState(0);
    const [premadeFilter, setPremadeFilter] = useState<PremadeEventLogFilterSet | undefined | null>(null);
    const [eventLogRows, setEventLogRows] = useState<GridRowModel[] | undefined>(undefined);
    const [debounceOn, setDebounceOn] = useState(false);
    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>();
    const [sortFirstLoad, setSortFirstLoad] = useState(true);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'documentSendReceiveDate',
            sort: 'desc',
        },
    ]);
    const [serverSortModel, setServerSortModel] = useState<MessageLogOrderByInput[] | undefined>([{
        createdTime: OrderDirection.Desc
    } as MessageLogOrderByInput]);
    const [pageSize, setPageSize] = useState(defaultGridListPageSize);
    const [totalCount, setTotalCount] = useState(0);
    
    const eventLogs = useAppSelector(selectEventLogList);
    const error = useAppSelector(selectError);
    const requestsInFlight = useAppSelector(selectRequestsInFlight);
    const eventLogsPagingResult = useAppSelector(selectEventLogPagingResult);
    
    const canEdit = viewerCanEdit(viewer);
    
    useTitle("System Event Logs");

    const debounceOnSortingChanged = useRef(
        debounce((servSortModel) => {
            onSortModelChanged(servSortModel);
        }, 1000)
    ).current;

    useEffect(() => {
        dispatch(clearState());
        setTimeout(() => {
            setDebounceOn(true);
        }, 200);
        if (premadeQuery) {
            applyPreset();
        } else {
            setPremadeFilter(undefined);
        }
    }, []);

    useEffect(() => {
        if (totalCount > 0) {
            // Refresh list for new page size
            loadPage(undefined);
        }
    }, [pageSize]);

    useEffect(() => {
        if (eventLogsPagingResult?.totalCount) {
            setTotalCount(eventLogsPagingResult.totalCount);
        }
    }, [eventLogsPagingResult?.totalCount]);

    useEffect(() => {
        setEventLogRows(getEventLogRows());
        if (!eventLogs && requestsInFlight > 0) {
            setIsLoading(true);
        } else if (requestsInFlight > 0) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [eventLogs, requestsInFlight]);

    useEffect(() => {
        // we have content, so lets properly size that content area        
        const titleHeight = document.getElementById('event-logs-title-comp')?.clientHeight || 0;
        // if filters are open, include that in the calculation
        let filterBarHeight = 0;
        if (filtersOpen) {
            filterBarHeight = document.getElementById('event-logs-filter-bar')?.clientHeight || 0;
        }
        let totalHeaderAreaHeight = titleHeight + filterBarHeight;
        if (totalHeaderAreaHeight > 0) {
            setContentAreaHeight(`calc(100% - ${totalHeaderAreaHeight}px)`);
        }
    }, [eventLogs, filtersOpen]);

    useEffect(() => {
        if (eventLogs) {
            let numFilters = 0;

            if (filterClientName && filterClientName.length > 0) {
                numFilters += 1;
            }
            if (filterDirection && filterDirection.length > 0) {
                numFilters += 1;
            }
            if (filterDocumentType && filterDocumentType.length > 0) {
                numFilters += 1;
            }
            if (filterPartnerName && filterPartnerName.length > 0) {
                numFilters += 1;
            }
            if (filterPO && filterPO.length > 0) {
                numFilters += 1;
            }
            if (filterDocNumber && filterDocNumber.length > 0) {
                numFilters += 1;
            }
            if (filterStatus && filterStatus.length > 0) {
                numFilters += 1;
            }
            if (filterProcessName && filterProcessName.length > 0) {
                numFilters += 1;
            }
            if (filterFileName && filterFileName.length > 0) {
                numFilters += 1;
            }
            if (filterDateFrom) {
                numFilters += 1;
            }
            if (filterDateTo) {
                numFilters += 1;
            }

            setFilterCount(numFilters);
        }
    }, [eventLogs, filterClientName, filterDirection, filterDocumentType, filterPartnerName, filterPO, filterDocNumber, filterStatus, filterProcessName, filterDateFrom, filterDateTo, filterFileName]);

    useEffect(() => {
        return () => {
            debounceOnSortingChanged.cancel();
        };
    }, [debounceOnSortingChanged]);

    const getSortRefreshInput = () => {
        return {
            after: undefined,
            limit: pageSize,
            clientName: filterClientName,
            clientId: filterClientId,
            direction: filterDirection,
            documentType: filterDocumentType,
            order: serverSortModel,
            partnerName: filterPartnerName,
            partnerId: filterPartnerId,
            pO: filterPO,
            documentId: filterDocNumber,
            status: filterStatus,
            processName: filterProcessName,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            originalFileName: filterFileName
        } as GetMessagingLogsInput
    }

    useEffect(() => {
        if (!sortFirstLoad) {
            const sortRefreshData = getSortRefreshInput();
            if (debounceOn !== true) {
                onSortModelChanged(sortRefreshData);
            } else {
                debounceOnSortingChanged(sortRefreshData);
            }
        } else {
            setSortFirstLoad(false);
        }
    }, [serverSortModel]);
    
    const getEventLogRows = () => {
        if (eventLogs && eventLogs.length > 0) {
            return eventLogs.map((eventLog) => {
                const {
                    id,
                    client,
                    documentReferenceId,
                    pO,
                    documentDate,
                    documentType,
                    direction,
                    isAcknowledged,
                    createdTime,
                    processName,
                    documentId,
                    originalFileName,
                    status,
                    partner,
                    clientPartner,
                    rawMessage,
                } = eventLog;
                
                return {
                    _raw: eventLog,
                    id,
                    clientName: client?.name,
                    clientId: client?.id,
                    documentNumber: documentId,
                    PONumber: pO,
                    documentDate,
                    documentType,
                    direction,
                    isAcknowledged,
                    documentSendReceiveDate: createdTime,
                    referenceId: documentReferenceId,
                    partnerName: partner?.name,
                    partnerId: partner?.id,
                    clientPartnerId: clientPartner?.id,
                    fileName: originalFileName,
                    processName,
                    status,
                    rawMessage
                } as GridRowModel;
            }) as GridRowModel[];
        } else {
            return [];
        }
    };

    const onRowClientNameClick = (id: string | undefined) => {
        if (id) {
            let route = "/client/" + id;
            navigate(route);
        }
    };

    const onRowPartnerNameClick = (id: string | undefined) => {
        if (id) {
            let route = "/partner/" + id;
            navigate(route);
        }
    };

    const onRowDocumentTypeClick = (clientPartnerId: string | undefined) => {
        // navigate to the clientPartner to allow the user to view the associated transaction/documentType
        if (clientPartnerId) {
            let route = "/clientpartner/" + clientPartnerId;
            navigate(route);
        }
    };

    const onRowStatusClick = (rowId: string) => {
        if (rowId) {
          navigate('/messaginglog/' + rowId);
        }
    };
 
    const eventLogColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                headerName: 'CLIENT',
                field: 'clientName',
                flex: 2,
                minWidth: 180,
                cellClassName: "ediDataGridCellFirstChild",
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const clientId = row.clientId;
                    
                    if (clientId) {
                        return (
                            <Link
                                component="button"
                                className="ediDataGridCellFirstChild ediDataGridCellOverflowHidden"
                                onClick={() => {
                                    onRowClientNameClick(clientId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'DOC #',
                field: 'documentNumber', //documentId
                flex: 1,
                minWidth: 100,
                sortable: true,
            }, {
                headerName: 'PO #',
                field: 'PONumber',
                flex: 1,
                minWidth: 130,
                sortable: true,
            }, {
                headerName: 'I/O',
                field: 'direction',
                width: 60,
                sortable: false,
            }, {
                headerName: 'TYPE',
                field: 'documentType',
                width: 90,
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const clientPartnerId = row.clientPartnerId;
                    
                    if (clientPartnerId) {
                        return (
                            <Link
                                component="button"
                                variant="body2"
                                className="ediDataGridCellOverflowHidden"
                                onClick={() => {
                                    onRowDocumentTypeClick(clientPartnerId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'ACK',
                field: 'isAcknowledged',
                width: 60,
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let acknowledged = value === true ? "Y" : "";
                    return (
                        acknowledged
                    );
                },
                sortable: false,
            }, {
                headerName: 'DOC DATE',
                field: 'documentDate',
                width: 114,
                type: 'dateTime',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let dateValue = value ? getShortDateString(value) : undefined;
                    let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                    return (
                        <GridCellDualVert header={dateValue} sub={timeValue} />
                    )
                },
                sortable: false,
            }, {
                headerName: 'DOC S/R',
                field: 'documentSendReceiveDate', //createdTime
                width: 114,
                sortable: true,
                type: 'dateTime',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let dateValue = value ? getShortDateString(value) : undefined;
                    let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                    return (
                        <GridCellDualVert header={dateValue} sub={timeValue} />
                    )
                },
            }, {
                headerName: 'REF #',
                field: 'referenceId',
                flex: 2,
                minWidth: 200,
                sortable: true,
            }, {
                headerName: 'PARTNER',
                field: 'partnerName',
                minWidth: 140,
                flex: 2,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const partnerId = row.partnerId;
                    if (partnerId) {
                        return (
                            <Link
                                component="button"
                                variant="body2"
                                className="ediDataGridCellOverflowHidden"
                                onClick={() => {
                                    onRowPartnerNameClick(partnerId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'FILE NAME',
                field: 'fileName',
                flex: 2,
                minWidth: 200,
                sortable: false,
            }, {
                headerName: 'PROCESS NAME',
                field: 'processName',
                flex: 2,
                minWidth: 180,
                sortable: false,
                valueGetter: (params: GridValueGetterParams) => {
                    const { value } = params;
                    return capitalizeWithUnderscoreRemoval(value) ?? '';
                },
            }, {
                headerName: 'STATUS',
                field: 'status',
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const displayValue = capitalizeWithUnderscoreRemoval(value, ' ');
                    if (value === DocumentStatus.Error) {
                        var truncatedMessage = row.rawMessage && row.rawMessage.length > 300 ? row.rawMessage.substring(0, 300) + "..." : row.rawMessage;
                        return (
                            <Tooltip
                                title={<Typography style={{fontSize: '14px'}}>{truncatedMessage}</Typography>}
                                placement='left-start'
                            >
                                <DetailsLink onClick={() => {onRowStatusClick(row.id)}} color="error" >{displayValue}</DetailsLink>
                            </Tooltip>
                        )
                    } else if (value === DocumentStatus.ResolvedError) {
                        return (
                            <DetailsLink onClick={() => {onRowStatusClick(row.id)}} color="primary" >{displayValue}</DetailsLink>
                        )
                    }
                    else {
                        return displayValue;
                    }
                },
            }, {
                headerName: 'MESSAGE',
                field: 'rawMessage',
                hide: true,
            },

        ],
        [],
    );

    const GetApiRef = () => {
        return useGridApiContext();
    }
    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the System Event Logs' error={error}></ErrorMessage>
                )}
                {!error && (eventLogs && eventLogs.length > 0 && requestsInFlight > 0) && (
                    <CircularProgress aria-label={'progress spinner'} key={'logs-spinner'} size={42} sx={{ zIndex: 1 }} />
                )}
                {!error && (!eventLogs) && (
                    <MainDataGridLoadingSkeleton apiRef={GetApiRef()} rowBottomMargin={6} />
                )}
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the System Event Logs' error={error}></ErrorMessage>
                )}
                {!error && (eventLogs && eventLogs.length <= 0 && filterCount > 0) && (
                    <NoResultsMessage topMargin={6} message="Try different filters to see results." />
                )}
            </GridOverlay>
        );
    };

    const getServerSortEntryFromGridSortEntry = (entry: GridSortItem) => {
        let newModel = {} as MessageLogOrderByInput;
        switch (entry.field) {
            case 'documentSendReceiveDate':
                newModel.createdTime = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'documentNumber':
                newModel.documentId = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'referenceId':
                newModel.documentReferenceId = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'documentType':
                newModel.documentType = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'PONumber':
                newModel.purchaseOrderNumber = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'internalClientCode':
                newModel.internalClientCode = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'internalPartnerCode':
                newModel.internalPartnerCode = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            
        }
        return newModel;
    };

    const createdAndSetServerSortModel = (model: GridSortModel) => {
        if (model && model.length > 0) {
            let newArray = [] as MessageLogOrderByInput[];
            let i = 0;
            for (i; i < model.length; ++i) {
                newArray.push(getServerSortEntryFromGridSortEntry(model[i]));
            }
            setServerSortModel(newArray);
        } else {
            setServerSortModel(undefined);
        }

    };

    const onSortModelChange = (model: GridSortModel) => {
        createdAndSetServerSortModel(model);
        setSortModel(model);
    };

    const onFiltersClick = () => {
        // Show/hide filters bar
        setFiltersOpen(!filtersOpen);
        // reset filters cleared to false since not clicking close button on bar at this point
        setFiltersCleared(false);
    };

    const onFilterBarClose = () => {
        // set filters as cleared
        setFiltersCleared(true);
        setFilterCount(0);
        // we don't want to close the filter bar since client is required
        setFiltersOpen(true);
    };

    const onSortModelChanged = (refreshInput: GetMessagingLogsInput) => {
        dispatch(clearState());
        dispatch(fetchEventLogList(refreshInput));
    };

    const onSelectionModelChange = (currentSelectionModel: GridSelectionModel) => {
        setSelectionModel(currentSelectionModel);
    };

    const loadPage = (endEdge: string | undefined) => {
        dispatch(fetchEventLogList({
            after: endEdge,
            limit: pageSize,
            clientName: filterClientName,
            clientId: filterClientId,
            direction: filterDirection,
            documentType: filterDocumentType,
            order: serverSortModel,
            partnerName: filterPartnerName,
            partnerId: filterPartnerId,
            pO: filterPO,
            documentId: filterDocNumber,
            status: filterStatus,
            processName: filterProcessName,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            originalFileName: filterFileName
        }));
    };

    const handlePageLoad = () => {
        if (!eventLogsPagingResult) {
            return;
        }
        if (!eventLogsPagingResult.cursor?.nextPage) {
            return;
        }

        loadPage(eventLogsPagingResult.cursor.nextPage);
    };

    const refreshFilters = (
        filterClient: string | undefined,
        filterIdOfClient?: string | undefined,
        filterIO?: string | undefined,
        filterDocType?: string | undefined,
        filterPartner?: string | undefined,
        filterIdOfPartner?: string | undefined,
        filterPONum?: string | undefined,
        filterDocNum?: string | undefined,
        filterStatusVal?: DocumentStatus | undefined,
        filterProcess?: ProcessName | undefined,
        filterDocDateFrom?: DateTime | undefined,
        filterDocDateTo?: DateTime | undefined,
        filterOriginalFileName?: string | undefined,
    ) => {
        setFilterClientName(filterClient);
        setFilterClientId(filterIdOfClient);
        setFilterDirection(filterIO);
        setFilterDocumentType(filterDocType);
        setFilterDocNumber(filterDocNum);
        setFilterPartnerName(filterPartner);
        setFilterPartnerId(filterIdOfPartner);
        setFilterPO(filterPONum);
        setFilterStatus(filterStatusVal);
        setFilterProcessName(filterProcess);
        setFilterDateFrom(filterDocDateFrom);
        setFilterDateTo(filterDocDateTo);
        setFilterFileName(filterOriginalFileName);
        dispatch(clearState());
        
        dispatch(fetchEventLogList({
            after: undefined,
            limit: pageSize,
            clientName: filterClient,
            clientId: filterIdOfClient,
            direction: filterIO,
            documentType: filterDocType,
            order: serverSortModel,
            partnerName: filterPartner,
            partnerId: filterIdOfPartner,
            pO: filterPONum,
            documentId: filterDocNum,
            status: filterStatusVal,
            processName: filterProcess,
            dateFrom: filterDocDateFrom,
            dateTo: filterDocDateTo,
            originalFileName: filterOriginalFileName
        }));
       
    };
   
    const applyPreset = () => {
        if (premadeQuery) {
            if (premadeQuery === "errors") {
                applyPresetErrorsFilter();
            } else if (premadeQuery === "map-errors") {
                applyPresetErrorsFilter(true);
            }
        }
    };
    
    const applyPresetErrorsFilter = (isMapping?: boolean) => {
        setPremadeFilter({
            filterStatusVal: DocumentStatus.Error,
            filterDocDateFrom: eventLogErrorsFilterDate,
            filterProcess: isMapping === true ? ProcessName.MapDataValidation : undefined
        } as PremadeEventLogFilterSet);
    };

    const handleExport = (options: GridCsvExportOptions, apiRef: React.MutableRefObject<GridApi>) => {
        // use the apiRef to directly call the export function
        // doing this instead of using the export button in the grid toolbar
        // to prevent having to choose the download option from the button dropdown
        apiRef?.current.exportDataAsCsv(options);
    };
        
    const gridToolbar = () => {
        let apiRef=GetApiRef();
        return (
            <GridToolbarContainer>
                <ToolbarButton
                    title="Export to CSV"
                    aria-label="Export"
                    size="small"
                    onClick={() => handleExport({ allColumns: true }, apiRef)}
                    color='primary'
                    disabled={isLoading || !eventLogRows || eventLogRows.length === 0}
                >
                    Export
                </ToolbarButton>
            </GridToolbarContainer>
        );
    };

    const customFooter = () => {
        return (
            <GridListFooter
                pageSize={pageSize}
                onPageSizeChange={(newPageSize: number) => {
                    setPageSize(newPageSize);
                }}
            />
        );
    };

    return (
        <MainContentBox>
            <PageTitleBar text='System Event Logs' id="event-logs-title-comp">
                <PageTitleToolbarGrid item>
                    <FiltersButton
                        onClick={onFiltersClick}
                        filterCount={filterCount}
                        filtersCleared={filtersCleared}
                        disabled={isLoading}
                        aria-label="filter button"
                        data-cy="filters"
                    />
                </PageTitleToolbarGrid>
            </PageTitleBar>
            <EventLogsFilterBar
                id="event-logs-filter-bar"
                visible={filtersOpen}
                loading={isLoading}
                viewer={viewer}
                premadeSet={premadeFilter}
                onFilterChanged={refreshFilters}
                onClose={onFilterBarClose}
            />
            <DataGridListScrollBox scrollheight={contentAreaHeight}>
                <MainDataGridNoRowHover
                    loading={isLoading}
                    rowHeight={52}
                    aria-label="Event Logs List"
                    rowCount={totalCount}
                    hideFooterSelectedRowCount
                    disableColumnMenu
                    disableColumnFilter
                    checkboxSelection={canEdit}
                    rows={eventLogRows ?? []}
                    columns={eventLogColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    sortingMode="server"
                    onSortModelChange={onSortModelChange}
                    selectionModel={selectionModel}
                    onSelectionModelChange={onSelectionModelChange}
                    onRowsScrollEnd={handlePageLoad}
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        Toolbar: gridToolbar,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        Footer: customFooter
                    }}
                />
            </DataGridListScrollBox>
        </MainContentBox>
    );
}

export default EventLogs;